import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import classNames from 'classnames';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';

import {
  deriveEmptyEdgesArrayFromNumberOfBins,
  deriveEmptyWeightsArrayFromNumberOfBins, padEndOfArrayWithNulls,
  transformBinEdgesToTableData,
} from '../../../utils/assay_utils';

import Label from '../../common/Labels/Label';
import SmallTable from './SmallTable';

const getDisplayValueForFromToColumn = (cellValue, isEnabled = false) => {
  if (!isEnabled) {
    return '';
  }
  if (cellValue === 'Min' || cellValue === 'Max') {
    return cellValue
  }
  const numericValue = parseFloat(cellValue)
  if (isNumber(numericValue) && !isNaN(numericValue)) {
    return numericValue;
  }
  return '';
}

const getDisplayValueForWeightsColumn = (cellValue, isEnabled = false) => {
  if (isEnabled) {
    const numericValue = parseFloat(cellValue)
    if (isNumber(numericValue) && !isNaN(numericValue)){
      return numericValue;
    }
    return '';
  }
  return 'Equal Weight';
}

const LabelCellRenderer = ({ value }) => <Label className="h-full flex items-center text-center p-2">{value}</Label>

const EditableCellRenderer = ({
  value: initialValue, row: { index }, column: { id }, meta = {},
}) => {
  const [value, setValue] = useState(initialValue)

  const isFromToColumn = id === 'fromValue' || id === 'toValue';
  const isBinWeightsColumn = id === 'binWeight';
  const isMinMaxConstant = value === 'Max' || value === 'Min';

  const useCustomBinEdges = meta?.useCustomBinEdges;
  const useCustomWeights = meta?.useCustomWeights;
  
  const disableFromToColumn = isFromToColumn && !useCustomBinEdges;
  const disableWeightsColumn = isBinWeightsColumn && !useCustomWeights;

  const onChange = (e) => {
    meta?.updateCellValue(index, id, e.target.value)
  };

  // If the initialValue is changed externally, sync it up with our state
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const displayValue = isFromToColumn
    ? getDisplayValueForFromToColumn(value, useCustomBinEdges)
    : isBinWeightsColumn
      ? getDisplayValueForWeightsColumn(value, useCustomWeights)
      : value;

  return (
    <input
      type={(isMinMaxConstant || displayValue === 'Equal Weight') ? 'text' : 'number'}
      placeholder="--"
      className={classNames(
        'align-left h-full w-full p-2',
        { 'font-bold': displayValue === 'Min' || displayValue === 'Max' },
        { 'bg-transparent': !disableFromToColumn && !disableWeightsColumn },
        { 'bg-border-light': disableFromToColumn || disableWeightsColumn },
      )}
      min={0}
      step={0.1}
      value={displayValue}
      onChange={onChange}
      disabled={disableFromToColumn || disableWeightsColumn || (isFromToColumn && isMinMaxConstant)}
    />
  )
}

// Assume Component will be provided an API for setting edges and weights by index
// Sizes of Edge/Weights arrays will be generated based on the specified number of bins
// Num Weights = Num Bins + 2 (number of rows including outliers)
// Num Edges = Num Bins + 1 (number of unique values excluding Min/Max)
export const AssayCustomBinningTable = ({
  binEdges,
  binWeights,
  setEdgeByIndex,
  setWeightByIndex,
  useCustomBinEdges = false,
  useCustomWeights = false,
}) => {
  const tableData = transformBinEdgesToTableData(binEdges, binWeights);

  const defaultColumn = React.useMemo(
    () => ({
      Cell: EditableCellRenderer,
      width: 64,
    }),
    []
  )

  const columns = useMemo(() => [
    {
      Header: 'Bin Value',
      accessor: 'binLabel',
      Cell: LabelCellRenderer,
      width: 64
    },
    {
      Header: 'From',
      accessor: 'fromValue',
      Cell: EditableCellRenderer,
      width: 40
    },
    {
      Header: 'To',
      accessor: 'toValue',
      Cell: EditableCellRenderer,
      width: 40,
    },
    {
      Header: 'Bin Weight',
      accessor: 'binWeight',
      Cell: EditableCellRenderer,
      width: 64,
    },
  ], []);

  const updateCellValue = (rowIndex, columnId, cellValue) => {
    const numericValue = parseFloat(cellValue);
    const assignmentValue = isNumber(numericValue) ? numericValue : null;
    if (columnId === 'fromValue') {
      setEdgeByIndex(rowIndex - 1, assignmentValue);
    }
    if (columnId === 'toValue') {
      setEdgeByIndex(rowIndex, assignmentValue);
    }
    if (columnId === 'binWeight') {
      setWeightByIndex(rowIndex, assignmentValue);
    }
  }

  return (
    <SmallTable
      columns={columns}
      data={tableData}
      defaultColumn={defaultColumn}
      meta={{
        useCustomWeights,
        useCustomBinEdges,
        updateCellValue,
      }}
    />
  )
}

const StatefulBinningTable = ({
  setEdges,
  setWeights,
  numberOfBins = 1,
  useCustomBinEdges = false,
  useCustomWeights = false,
}) => {
  const [customWeights, setCustomWeights] = useState(deriveEmptyEdgesArrayFromNumberOfBins(numberOfBins));
  const [customEdges, setCustomEdges] = useState(deriveEmptyWeightsArrayFromNumberOfBins(numberOfBins));

  const setEdgeByIndex = useCallback((index, value) => {
    const newEdges = [...customEdges];
    newEdges[index] = value;
    setCustomEdges(newEdges);
  }, [customEdges]);

  const setWeightByIndex = useCallback((index, value) => {
    const newWeights = [...customWeights];
    newWeights[index] = value;
    setCustomWeights(newWeights);
  }, [customWeights]);

  const numberOfCustomWeights = customWeights.length;
  const numberOfCustomEdges = customEdges.length;

  // Updates the Custom Weights array if the number of bins changes
  useEffect(() => {
    const expectedNumberOfWeights = numberOfBins + 2;
    if (numberOfCustomWeights !== expectedNumberOfWeights) {
      if (expectedNumberOfWeights < numberOfCustomWeights) {
        setCustomWeights(customWeights.slice(0, expectedNumberOfWeights));
      } else {
        setCustomWeights(padEndOfArrayWithNulls(customWeights, expectedNumberOfWeights - numberOfCustomWeights));
      }
    }
  }, [customWeights, numberOfBins, numberOfCustomWeights]);

  // Updates the Custom Edges array if the number of bins changes
  useEffect(() => {
    const expectedNumberOfEdges = numberOfBins + 1;
    if (numberOfCustomEdges !== expectedNumberOfEdges) {
      if (expectedNumberOfEdges < numberOfCustomEdges) {
        setCustomEdges(customEdges.slice(0, expectedNumberOfEdges));
      } else {
        setCustomEdges(padEndOfArrayWithNulls(customEdges, expectedNumberOfEdges - numberOfCustomEdges));
      }
    }
  }, [customEdges, numberOfBins, numberOfCustomEdges]);

  // Assign Custom Bin Weights to Advanced Settings based on flag state
  useEffect(() => {
    if (useCustomWeights) {
      setWeights(customWeights);
    } else {
      setWeights(null);
    }
  }, [useCustomWeights, customWeights, setWeights]);

  // Assign Custom Bin Edges to Advanced Settings based on flag state
  useEffect(() => {
    if (useCustomBinEdges) {
      setEdges(customEdges);
    } else {
      setEdges(null);
    }
  }, [customEdges, setEdges, useCustomBinEdges])

  return (
    <AssayCustomBinningTable
      binEdges={customEdges}
      binWeights={customWeights}
      setEdgeByIndex={setEdgeByIndex}
      setWeightByIndex={setWeightByIndex}
      useCustomBinEdges={useCustomBinEdges}
      useCustomWeights={useCustomWeights}
    />
  )
}

export default StatefulBinningTable;
